import { useState, useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Slider } from "@/components/ui/slider";
import { balanceAt, fmtCompact, fmtMoney } from "@/lib/finance";

const HORIZONS = [30, 60, 90, 180];

export const ProjectionTool = ({ tranches }) => {
  const [horizon, setHorizon] = useState(90);
  const rate = tranches[0]?.daily_rate ?? 0.05;

  const { data, projected } = useMemo(() => {
    const base = tranches.reduce((s, t) => s + t.current_balance, 0);
    const principal = tranches.reduce((s, t) => s + t.principal_amount, 0);
    const pts = [];
    const step = Math.max(1, Math.round(horizon / 30));
    for (let d = 0; d <= horizon; d += step) {
      pts.push({
        day: d,
        compound: base * Math.pow(1 + rate, d),
        simple: base + base * rate * d,
      });
    }
    return { data: pts, projected: base * Math.pow(1 + rate, horizon), principal };
  }, [tranches, horizon, rate]);

  return (
    <div className="glass rounded-xl border border-[#232D40] p-5 sm:p-6" data-testid="projection-chart-container">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-lg font-semibold">Forward Projection</h2>
          <p className="text-xs text-slate-500">A = P × (1 + {(rate * 100).toFixed(1)}%)<sup>t</sup> · compound vs simple</p>
        </div>
        <div className="flex gap-1.5">
          {HORIZONS.map((h) => (
            <button
              key={h} data-testid={`horizon-${h}-btn`} onClick={() => setHorizon(h)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono-data font-semibold transition-colors ${
                horizon === h ? "bg-amber-500 text-[#0B0E17]" : "bg-white/5 text-slate-400 hover:text-slate-200"
              }`}
            >{h}d</button>
          ))}
        </div>
      </div>

      <div className="flex items-end justify-between mb-3">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Projected in {horizon} days</p>
          <p className="text-2xl font-extrabold font-mono-data text-amber-400">{fmtMoney(projected, 0)}</p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ left: -8, right: 8, top: 6 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#232D40" vertical={false} />
          <XAxis dataKey="day" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false}
            tickFormatter={(d) => `${d}d`} />
          <YAxis stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} tickFormatter={fmtCompact} width={56} />
          <Tooltip
            contentStyle={{ background: "#0B0E17", border: "1px solid #232D40", borderRadius: 10, fontFamily: "JetBrains Mono" }}
            labelFormatter={(d) => `Day ${d}`} formatter={(v, n) => [fmtMoney(v, 0), n === "compound" ? "Compound" : "Simple"]}
          />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
          <Line type="monotone" dataKey="compound" name="Compound" stroke="#F59E0B" strokeWidth={2.5} dot={false} />
          <Line type="monotone" dataKey="simple" name="Simple" stroke="#475569" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-5 px-1">
        <div className="flex justify-between text-xs text-slate-400 mb-2 font-mono-data">
          <span>Horizon: {horizon} days</span><span>30 → 365</span>
        </div>
        <Slider
          data-testid="custom-days-slider" value={[horizon]} min={30} max={365} step={5}
          onValueChange={(v) => setHorizon(v[0])}
        />
      </div>
    </div>
  );
};
